import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { SIGNABLE_NAME_MAX_LENGTH } from '../../../enums/Constraints';
import { SignableSchema } from '../../../definitions/schemas';
import FormValidatorHelper from '../../../Services/FormValidatorService';
import SignablesInterface from '../../../graphql/Signables/SignablesInterface';
import SnackbarStore from '../../../stores/observables/SnackbarStore';

interface Props {
  name: string;
  onClose: () => void;
  onRenamed: (signable: SignableSchema) => void;
  open: boolean;
  signableId: string;
}

const RenameDialog: React.FC<Props> = (props: Props): JSX.Element => {
  const { name: initialName, onClose, onRenamed, open, signableId } = props;

  const [name, setName] = useState(initialName);

  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { current: validator } = useRef(new FormValidatorHelper());

  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => {
        setSaving(false);
        setSubmitted(false);
        setName('');
      }, 250);

      return () => {
        clearTimeout(timeout);
      };
    }

    setName(initialName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitted(true);

    if (validator.isValid()) {
      onRename();
    }
  };

  const onRename = async () => {
    try {
      setSaving(true);

      const res = await new SignablesInterface().update({
        id: signableId,
        name,
      });

      onClose();
      onRenamed(res.updateSignable.signable);
    } catch (e) {
      SnackbarStore.show(`${e}`);
      setSaving(false);
    }
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const max = `max:${SIGNABLE_NAME_MAX_LENGTH}`;
  validator.check('name', name, `required|${max}`);

  const nameError = validator.getError('name', submitted);

  return (
    <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
      <DialogTitle>Rename signable document</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box mb={2}>
            <TextField
              autoFocus
              color="secondary"
              disabled={saving}
              error={!!nameError}
              fullWidth
              helperText={nameError}
              inputProps={{
                maxLength: SIGNABLE_NAME_MAX_LENGTH,
              }}
              label="Name"
              onChange={onNameChange}
              size="small"
              required
              value={name}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="secondary"
            disabled={saving}
            type="submit"
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RenameDialog;
